import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from './ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { Button } from './ui/button';
import { MoreVertical, Pencil, Trash2, Archive, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable Edit / Delete / Archive action menu for any admin-managed record.
 *
 * Props:
 *   - canEdit, canDelete, canArchive: booleans, role-aware gating
 *   - deleteUrl: full path (e.g., '/api/admin/crm/internal-notes/abc123')
 *   - deleteConfirmTitle, deleteConfirmDescription: confirmation copy
 *   - onEdit: () => void  (called when admin clicks Edit; parent owns the edit dialog)
 *   - onArchive: () => Promise<void>  (called for archive)
 *   - onDeleted: () => void  (called after successful delete to refresh parent)
 *   - headers: axios auth headers
 *   - testIdPrefix: data-testid prefix (e.g., 'note', 'address')
 *   - entityId: id used to build test ids (e.g., 'note-actions-{id}')
 */
const RecordActions = ({
  canEdit = true, canDelete = true, canArchive = false,
  deleteUrl, deleteConfirmTitle = 'Delete record?',
  deleteConfirmDescription = 'This action cannot be undone.',
  onEdit, onArchive, onDeleted, onCustomDelete,
  headers, testIdPrefix = 'record', entityId = '',
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [forceState, setForceState] = useState(null); // { message, recommended_action }

  const doDelete = async (force = false) => {
    setDeleting(true);
    try {
      const url = force ? `${API_URL}${deleteUrl}${deleteUrl.includes('?') ? '&' : '?'}force=true` : `${API_URL}${deleteUrl}`;
      await axios.delete(url, { headers });
      toast.success(force ? 'Force-deleted' : 'Deleted');
      setConfirmOpen(false);
      setForceState(null);
      onDeleted?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg = 'Delete failed';
      let canForce = false;
      if (typeof detail === 'string') msg = detail;
      else if (detail && typeof detail === 'object') {
        msg = detail.message || msg;
        canForce = !!detail.can_force;
      }
      if (err.response?.status === 409 && canForce) {
        // Offer Force Delete escalation to master admins
        setForceState({ message: msg });
      } else {
        toast.error(msg);
      }
    } finally {
      setDeleting(false);
    }
  };

  if (!canEdit && !canDelete && !canArchive) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-zinc-400 hover:text-zinc-700"
            data-testid={`${testIdPrefix}-actions-${entityId}`}>
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-40">
          {canEdit && onEdit && (
            <DropdownMenuItem onClick={onEdit} data-testid={`${testIdPrefix}-edit-${entityId}`}>
              <Pencil className="h-3.5 w-3.5 mr-2" /> Edit
            </DropdownMenuItem>
          )}
          {canArchive && onArchive && (
            <DropdownMenuItem onClick={onArchive} data-testid={`${testIdPrefix}-archive-${entityId}`}>
              <Archive className="h-3.5 w-3.5 mr-2" /> Archive
            </DropdownMenuItem>
          )}
          {(canEdit || canArchive) && canDelete && <DropdownMenuSeparator />}
          {canDelete && (
            <DropdownMenuItem
              className="text-red-600 focus:text-red-700 focus:bg-red-50"
              onClick={() => { if (onCustomDelete) { onCustomDelete(); } else { setConfirmOpen(true); } }}
              data-testid={`${testIdPrefix}-delete-${entityId}`}
            >
              <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirmOpen} onOpenChange={(o) => { setConfirmOpen(o); if (!o) setForceState(null); }}>
        <AlertDialogContent data-testid={`${testIdPrefix}-delete-confirm-${entityId}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{forceState ? 'Force delete this record?' : deleteConfirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {forceState
                ? <>
                    <span className="block mb-2 text-red-600 font-semibold">The system refused because:</span>
                    <span className="block mb-3 text-zinc-700 italic">&quot;{forceState.message}&quot;</span>
                    <span className="block">As master admin, you can force-delete this record anyway. This bypasses all safety checks and immediately removes the record and its non-financial descendants. This cannot be undone.</span>
                  </>
                : deleteConfirmDescription}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={(e) => { e.preventDefault(); doDelete(!!forceState); }}
              disabled={deleting}
              data-testid={`${testIdPrefix}-delete-confirm-btn-${entityId}`}
            >
              {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              {forceState ? 'Force Delete' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default RecordActions;
